/**
 * Hand an exported Character to the iOS share sheet, or to a download when sharing files is not
 * available (desktop browsers, older Safari). Import reads a picked file. Share-target (receiving
 * a file from another app) needs the web app manifest and service worker from #3.
 */
import type { Character } from "./model";
import { EXPORT_MIME, exportFileName, makeEnvelope, serializeEnvelope } from "./transfer";

export type ShareOutcome = "shared" | "downloaded" | "cancelled";

export async function shareCharacter(character: Character, appVersion: string): Promise<ShareOutcome> {
  const text = serializeEnvelope(makeEnvelope(character, appVersion));
  const file = new File([text], exportFileName(character), { type: EXPORT_MIME });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: character.name });
      return "shared";
    } catch (e) {
      if ((e as Error).name === "AbortError") return "cancelled";
      // Fall through to the download when the share sheet refuses the file.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.rel = "noopener";
    document.body.append(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return "downloaded";
}

/** Open the file picker for one Character file. Resolves to undefined when the user cancels. */
export function pickCharacterFile(): Promise<File | undefined> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = `${EXPORT_MIME},.json`;
    input.style.display = "none";
    input.onchange = () => {
      resolve(input.files?.[0]);
      input.remove();
    };
    input.oncancel = () => {
      resolve(undefined);
      input.remove();
    };
    document.body.append(input);
    input.click();
  });
}
